/**
 * Agency multi-user RBAC — mirror of the backend `AgencyPermission` / `AgencyRole` matrix.
 * The backend is the authority (it enforces every rule); the front only uses this to show/hide UI.
 */
export enum AgencyPermission {
  MISSION_VIEW = 'MISSION_VIEW',
  DOCUMENT_EXPORT = 'DOCUMENT_EXPORT',
  MISSION_WRITE = 'MISSION_WRITE',
  MISSION_CANCEL = 'MISSION_CANCEL',
  MISSION_DELETE = 'MISSION_DELETE',
  PLANNING_VIEW = 'PLANNING_VIEW',
  CANDIDATE_CONTACT = 'CANDIDATE_CONTACT',
  FINANCE_VIEW = 'FINANCE_VIEW',
  AUDIT_VIEW = 'AUDIT_VIEW',
  USER_INVITE = 'USER_INVITE',
  USER_DELETE = 'USER_DELETE',
  USER_MANAGE = 'USER_MANAGE',
  VALIDATE_PRO_ACCOUNT = 'VALIDATE_PRO_ACCOUNT',
  AGENCY_SETTINGS = 'AGENCY_SETTINGS',
}

export type AgencyRoleCode =
  | 'ADMIN'
  | 'ASSISTANT_SECRETAIRE'
  | 'MEDECIN_TITULAIRE'
  | 'CADRE_SANTE'
  | 'RESPONSABLE_RH'
  | 'AUTRE'
  // Anciens rôles (Q2 : non déployés) — reconnus en lecture seule le temps de la bascule.
  | 'GERANT'
  | 'RECRUTEUR'
  | 'SECRETAIRE';

export const ROLE_PERMISSIONS: Record<AgencyRoleCode, AgencyPermission[]> = {
  ADMIN: Object.values(AgencyPermission),
  // Matrice du PDF client « Spécifications fonctionnelles » §2 — miroir exact de AgencyPermission.MATRIX (back).
  // Défauts par rôle : toggles ajustables ensuite par utilisateur (Q6).
  ASSISTANT_SECRETAIRE: [
    AgencyPermission.MISSION_VIEW,
    AgencyPermission.MISSION_WRITE,
    AgencyPermission.MISSION_CANCEL,
    AgencyPermission.MISSION_DELETE,
    AgencyPermission.PLANNING_VIEW,
    AgencyPermission.CANDIDATE_CONTACT,
    AgencyPermission.DOCUMENT_EXPORT,
    AgencyPermission.USER_INVITE,
    AgencyPermission.VALIDATE_PRO_ACCOUNT,
  ],
  MEDECIN_TITULAIRE: [
    AgencyPermission.MISSION_VIEW,
    AgencyPermission.MISSION_WRITE,
    AgencyPermission.MISSION_CANCEL,
    AgencyPermission.MISSION_DELETE,
    AgencyPermission.PLANNING_VIEW,
    AgencyPermission.CANDIDATE_CONTACT,
    AgencyPermission.DOCUMENT_EXPORT,
  ],
  CADRE_SANTE: [
    AgencyPermission.MISSION_VIEW,
    AgencyPermission.MISSION_WRITE,
    AgencyPermission.MISSION_CANCEL,
    AgencyPermission.MISSION_DELETE,
    AgencyPermission.PLANNING_VIEW,
    AgencyPermission.CANDIDATE_CONTACT,
    AgencyPermission.DOCUMENT_EXPORT,
  ],
  RESPONSABLE_RH: [
    AgencyPermission.MISSION_VIEW,
    AgencyPermission.MISSION_WRITE,
    AgencyPermission.MISSION_CANCEL,
    AgencyPermission.MISSION_DELETE,
    AgencyPermission.PLANNING_VIEW,
    AgencyPermission.CANDIDATE_CONTACT,
    AgencyPermission.DOCUMENT_EXPORT,
    AgencyPermission.USER_INVITE,
    AgencyPermission.USER_DELETE,
    AgencyPermission.USER_MANAGE,
    AgencyPermission.VALIDATE_PRO_ACCOUNT,
  ],
  AUTRE: [],
  // Anciens rôles (Q2) : sets d'origine conservés.
  GERANT: [
    AgencyPermission.MISSION_VIEW,
    AgencyPermission.DOCUMENT_EXPORT,
    AgencyPermission.MISSION_WRITE,
    AgencyPermission.MISSION_CANCEL,
    AgencyPermission.MISSION_DELETE,
    AgencyPermission.CANDIDATE_CONTACT,
    AgencyPermission.FINANCE_VIEW,
    AgencyPermission.AUDIT_VIEW,
  ],
  RECRUTEUR: [
    AgencyPermission.MISSION_VIEW,
    AgencyPermission.DOCUMENT_EXPORT,
    AgencyPermission.MISSION_WRITE,
    AgencyPermission.MISSION_CANCEL,
    AgencyPermission.MISSION_DELETE,
    AgencyPermission.CANDIDATE_CONTACT,
  ],
  SECRETAIRE: [AgencyPermission.MISSION_VIEW, AgencyPermission.DOCUMENT_EXPORT],
};

export interface AgencyRoleOption {
  value: AgencyRoleCode;
  label: string;
  description: string;
  /** Lot G (G1) : rôles proposés à la création d'un collaborateur (les 5 + admin si édition). */
  selectable?: boolean;
}

export const AGENCY_ROLES: AgencyRoleOption[] = [
  {
    value: 'ADMIN',
    label: 'Administrateur du service',
    description: 'Tous les droits, non configurable. Réservé au dirigeant principal du compte du service.',
    selectable: false,
  },
  {
    value: 'ASSISTANT_SECRETAIRE',
    label: 'Assistant / Secrétaire de service',
    description: 'Créer des comptes, valider les comptes des professionnels, missions, plannings, documents, messagerie. Pas de suppression de comptes ni de modification des droits.',
    selectable: true,
  },
  {
    value: 'MEDECIN_TITULAIRE',
    label: 'Médecin titulaire',
    description: 'Missions, plannings, documents des professionnels, messagerie. Pas de gestion ni de validation des comptes.',
    selectable: true,
  },
  {
    value: 'CADRE_SANTE',
    label: 'Cadre de santé',
    description: 'Missions, plannings, documents des professionnels, messagerie. Pas de gestion ni de validation des comptes.',
    selectable: true,
  },
  {
    value: 'RESPONSABLE_RH',
    label: 'Responsable ressources humaines',
    description: 'Tous les droits de la matrice : comptes (créer, supprimer, modifier les droits, valider les pros), missions, plannings, documents, messagerie.',
    selectable: true,
  },
  {
    value: 'AUTRE',
    label: 'Autre',
    description: 'Aucun droit par défaut. Indiquez le nom du poste ; l’administrateur active ensuite les droits voulus pour ce compte.',
    selectable: true,
  },
  // Anciens rôles (Q2 : non déployés) — plus proposés à la création, reconnus en lecture.
  {
    value: 'GERANT',
    label: 'Gérant (ancien)',
    description: 'Ancien rôle, plus attribuable.',
    selectable: false,
  },
  {
    value: 'RECRUTEUR',
    label: 'Recruteur (ancien)',
    description: 'Ancien rôle, plus attribuable.',
    selectable: false,
  },
  {
    value: 'SECRETAIRE',
    label: 'Secrétaire (ancien)',
    description: 'Ancien rôle, plus attribuable.',
    selectable: false,
  },
];

/** Rôles proposés à la création/modification d'un collaborateur (G1 : les 5 rôles). */
export const SELECTABLE_ROLES: AgencyRoleOption[] = AGENCY_ROLES.filter((r) => r.selectable);

export function roleLabel(role?: string | null): string {
  return AGENCY_ROLES.find((r) => r.value === role)?.label ?? role ?? '';
}

/** Libellés des droits — mots du PDF client §2 (matrice) ; les 3 derniers sont hors matrice (admin). */
export const PERMISSION_LABELS: Record<string, string> = {
  [AgencyPermission.USER_INVITE]: 'Créer de nouveaux comptes',
  [AgencyPermission.USER_DELETE]: 'Supprimer des comptes',
  [AgencyPermission.USER_MANAGE]: 'Modifier les droits d’un compte',
  [AgencyPermission.VALIDATE_PRO_ACCOUNT]: 'Valider les comptes créés par les professionnels (via l’application mobile)',
  [AgencyPermission.MISSION_WRITE]: 'Créer des missions',
  [AgencyPermission.MISSION_VIEW]: 'Répondre aux missions (accepter / refuser)',
  [AgencyPermission.MISSION_CANCEL]: 'Annuler une candidature (acceptée et/ou signée)',
  [AgencyPermission.MISSION_DELETE]: 'Supprimer/Annuler une offre de mission (non encore affectée)',
  [AgencyPermission.PLANNING_VIEW]: 'Accéder aux plannings des professionnels de santé',
  [AgencyPermission.CANDIDATE_CONTACT]: 'Utiliser la messagerie',
  [AgencyPermission.DOCUMENT_EXPORT]: 'Accéder aux documents des professionnels',
  [AgencyPermission.FINANCE_VIEW]: 'Relevés d’heures',
  [AgencyPermission.AUDIT_VIEW]: 'Consulter le journal d’audit',
  [AgencyPermission.AGENCY_SETTINGS]: 'Paramètres du compte du service & facturation',
};

export interface PermissionInfo {
  /** Une phrase : à quoi sert ce droit. */
  summary: string;
  /** Les fonctionnalités que ce droit débloque — ce que l'utilisateur voit / peut faire dans l'application. */
  allows: string[];
  /** Précisions et limites. */
  notes?: string[];
}

/**
 * Contenu de la fenêtre « i » de l'écran « Modifier un collaborateur ».
 * Chaque ligne a été relevée dans le code (contrôles @perm côté serveur + contrôles d'écran) ;
 * la version détaillée (avec les routes) est dans docs/roles_et_droits.md — garder les deux synchrones.
 */
export const PERMISSION_INFO: Record<string, PermissionInfo> = {
  [AgencyPermission.USER_INVITE]: {
    summary: 'Ajouter des personnes.',
    allows: [
      '« Inviter un collaborateur » (Gestion des collaborateurs).',
      '« Renvoyer l’invitation » d’un compte en attente.',
      '« Ajouter un professionnel de santé » (Gestion des professionnels de santé).',
      'Accès à « Gestion des collaborateurs ».',
    ],
    notes: ['Vous ne pouvez inviter quelqu’un qu’avec un rôle qui n’a pas plus de droits que vous.'],
  },
  [AgencyPermission.USER_DELETE]: {
    summary: 'Supprimer des comptes.',
    allows: [
      'Supprimer un collaborateur (icône corbeille dans « Gestion des collaborateurs »).',
      'Accès à « Gestion des collaborateurs ».',
    ],
    notes: ['Le compte principal et votre propre compte ne peuvent pas être supprimés.'],
  },
  [AgencyPermission.USER_MANAGE]: {
    summary: 'Gérer les collaborateurs et leurs droits.',
    allows: [
      '« Modifier » un collaborateur : profil, rôle, fonction et droits (cet écran).',
      '« Désactiver le compte » / « Réactiver le compte ».',
      'Accès à « Gestion des collaborateurs ».',
    ],
    notes: [
      'Vous ne pouvez pas modifier vos propres droits ni votre propre rôle.',
      'Vous ne pouvez donner à un autre compte que des droits que vous avez vous-même.',
    ],
  },
  [AgencyPermission.VALIDATE_PRO_ACCOUNT]: {
    summary: 'Traiter les inscriptions des professionnels faites depuis l’application mobile.',
    allows: [
      'Accès à « Comptes professionnels » (menu, avec le nombre de comptes en attente).',
      '« Valider » : le compte devient actif et le professionnel est prévenu.',
      '« Refuser » : le professionnel est prévenu par e-mail (motif facultatif) et le compte est retiré.',
      '« Valider / refuser » depuis « Gestion des professionnels de santé ».',
    ],
  },
  [AgencyPermission.MISSION_WRITE]: {
    summary: 'Créer des missions et affecter directement un professionnel.',
    allows: [
      '« Créer une mission » et modifier une mission.',
      'Enregistrer un brouillon, « Publier la mission ».',
      'Ajouter un nouveau secteur ou poste dans le formulaire de mission.',
      '« Affectation directe » (menu).',
      '« Affecter mission » à un candidat disponible.',
    ],
  },
  [AgencyPermission.MISSION_VIEW]: {
    summary: 'Accepter ou refuser les candidatures, et gérer les contrats.',
    allows: [
      '« Accepter » un candidat sur une mission.',
      'Mission fragmentée : « Accepter cette candidature » et « Refuser cette candidature ».',
      '« Annuler la candidature » d’un candidat pas encore accepté.',
      'Contrats : « Enregistrer », « Valider et envoyer », « Envoyer maintenant » (relance).',
    ],
  },
  [AgencyPermission.MISSION_CANCEL]: {
    summary: 'Annuler une candidature déjà acceptée et/ou signée.',
    allows: [
      '« Annuler la candidature » d’un candidat déjà accepté ou signé.',
      'Mission fragmentée : « Annuler les dates acceptées » et « Annuler les dates affectées signées ».',
    ],
  },
  [AgencyPermission.MISSION_DELETE]: {
    summary: 'Supprimer ou annuler une offre de mission tant que personne n’y est affecté.',
    allows: [
      'Supprimer un brouillon de mission (icône corbeille).',
      '« Annuler cette offre de mission » (si personne n’est affecté).',
    ],
  },
  [AgencyPermission.PLANNING_VIEW]: {
    summary: 'Voir le planning des professionnels de santé.',
    allows: ['Onglet « Planning » dans la fiche d’un professionnel de santé.'],
  },
  [AgencyPermission.CANDIDATE_CONTACT]: {
    summary: 'Écrire aux professionnels de santé.',
    allows: ['Envoyer des messages (messagerie et « Envoyer un message au candidat »).'],
    notes: ['Sans ce droit, vous pouvez lire les messages mais pas écrire.'],
  },
  [AgencyPermission.DOCUMENT_EXPORT]: {
    summary: 'Voir les documents des professionnels de santé.',
    allows: ['Onglet « Documents » dans la fiche d’un professionnel de santé.'],
  },
  [AgencyPermission.FINANCE_VIEW]: {
    summary: 'Gérer les relevés d’heures.',
    allows: [
      'Accès à « Relevés d’heures » (menu).',
      '« Valider » ou « Rejeter » les relevés envoyés par les professionnels.',
      '« Régénérer », « Valider et envoyer » un relevé, « Paramètres du relevé ».',
      'Pastille des relevés à valider en haut de la page.',
    ],
  },
  [AgencyPermission.AUDIT_VIEW]: {
    summary: 'Voir qui a fait quoi, et quand.',
    allows: ['Accès à « Journal d’audit » (menu), avec filtre par action.'],
  },
};

/** Les 11 droits de la matrice du PDF, dans l'ordre du PDF (écran « Gestion des droits », §4). */
export const MATRIX_PERMISSIONS: AgencyPermission[] = [
  AgencyPermission.USER_INVITE,
  AgencyPermission.USER_DELETE,
  AgencyPermission.USER_MANAGE,
  AgencyPermission.VALIDATE_PRO_ACCOUNT,
  AgencyPermission.MISSION_WRITE,
  AgencyPermission.MISSION_VIEW,
  AgencyPermission.MISSION_CANCEL,
  AgencyPermission.MISSION_DELETE,
  AgencyPermission.PLANNING_VIEW,
  AgencyPermission.CANDIDATE_CONTACT,
  AgencyPermission.DOCUMENT_EXPORT,
];

/** Droits hors matrice client, attribuables par l'admin (pas de surface UI pour AGENCY_SETTINGS : non listé). */
export const EXTRA_PERMISSIONS: AgencyPermission[] = [
  AgencyPermission.FINANCE_VIEW,
  AgencyPermission.AUDIT_VIEW,
];

/** L'écran « Gestion des collaborateurs » s'ouvre dès qu'un des trois droits « comptes » est détenu. */
export const USER_ACCOUNT_PERMISSIONS: AgencyPermission[] = [
  AgencyPermission.USER_MANAGE,
  AgencyPermission.USER_INVITE,
  AgencyPermission.USER_DELETE,
];

export const USER_STATUS_LABELS: Record<string, string> = {
  PENDING: 'En attente',
  ACTIVE: 'Actif',
  DISABLED: 'Désactivé',
};

/** Same rules as the backend PasswordPolicy. */
export const PASSWORD_RULES: { label: string; test: (v: string) => boolean }[] = [
  { label: 'Au moins 12 caractères', test: (v) => (v || '').length >= 12 },
  { label: 'Une lettre majuscule', test: (v) => /[A-Z]/.test(v || '') },
  { label: 'Une lettre minuscule', test: (v) => /[a-z]/.test(v || '') },
  { label: 'Un chiffre', test: (v) => /\d/.test(v || '') },
  { label: 'Un caractère spécial', test: (v) => /[^A-Za-z0-9\s]/.test(v || '') },
];

export const STRONG_PASSWORD_PATTERN = /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[^A-Za-z0-9\s]).{12,128}$/;
